import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import { Autocomplete, Box, Button, MenuItem, Select, Tab, Tabs, TextField, Typography, Divider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import DefaultSelectClient from '../../../components/default-select-client';
import SaveIcon from '@mui/icons-material/Save';
import ContractFileDataTable from './components/contract-file-data-table';
import InputMask from 'react-input-mask';
import BrazilianNumberInput from '../../../components/brazilian-number-input';
import PageHeader from '../../../components/page-header';
import { useContractCreateEditState } from './context/contract-create-edit-context';
import { useContractCreateEditController } from './context/contract-create-edit-controller';


export function CreateEditContractComponent() {
    const {
        contractId,
        contractRequest,
        typeContract,
        categoryProcess,
        unidEntity,
        purchases,
        tabIndex,
        showValidationErrors
    } = useContractCreateEditState();

    const {
        handleOnChange,
        handleDateChange,
        handleSave,
        setTabIndex,
        setFiles
    } = useContractCreateEditController();

    const filesDataTable = () => {
        return (
            <React.Fragment>
                <ContractFileDataTable props={{
                    contractReqeust: contractRequest,
                    contractId: contractId,
                    setFiles: setFiles
                }} />
            </React.Fragment >
        );
    };

    const handleTabChange = (event: any, newTabIndex: number) => {
        setTabIndex(newTabIndex);
    };

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title={contractId ? "Editar Contrato" : "Novo Contrato"}
                    subtitle={contractId ? "Atualize as informações do contrato" : "Cadastre um novo contrato no sistema"}
                    breadcrumbs={[
                        { label: 'Contratos', href: '/contratos' },
                        { label: contractId ? 'Editar Contrato' : 'Novo Contrato' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column'
                    }} elevation={3}
                >

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={3}>
                            {/* Seção: Cliente */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Cliente
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12}>
                                <DefaultSelectClient required={true} clientId={contractRequest.clientId} handleOnChange={handleOnChange}></DefaultSelectClient>
                            </Grid>

                            {/* Seção: Identificação do Contrato */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Identificação do Contrato
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>
                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel required>Nº Empenho do Contrato</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-number-contract-effort"
                                    error={showValidationErrors && (!contractRequest.contractEffortNumber || contractRequest.contractEffortNumber.length === 0)}
                                    value={contractRequest.contractEffortNumber || ''}
                                    onChange={handleOnChange}
                                    required
                                />
                            </Grid>

                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel required>Nº Processo</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-process-number"
                                    error={showValidationErrors && (!contractRequest.processNumber || contractRequest.processNumber.length === 0)}
                                    value={contractRequest.processNumber || ''}
                                    onChange={handleOnChange}
                                    required
                                />
                            </Grid>

                            <Grid item lg={1} sm={6} md={2} xl={1}>
                                <InputLabel required>Exercício</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-year-contract"
                                    error={showValidationErrors && !contractRequest.yearContract}
                                    value={contractRequest.yearContract || ''}
                                    type={"number"}
                                    inputProps={{ min: moment().year() - 1, max: moment().year() + 10 }}
                                    onChange={handleOnChange}
                                    required
                                />
                            </Grid>
                            <Grid item lg={7} sm={12} md={6} xl={7}>
                                <InputLabel required>Compra ou Licitação</InputLabel>
                                <Autocomplete
                                    fullWidth
                                    options={purchases}
                                    value={purchases.find(p => p.id === contractRequest.purchaseId) || null}
                                    onChange={(_event, newValue) => {
                                        handleOnChange({
                                            target: {
                                                name: 'select-purchase',
                                                value: newValue?.id ?? ''
                                            }
                                        });
                                    }}
                                    getOptionLabel={(option) =>
                                        `Nº ${option.number} | Processo: ${option.numberProcess} | Ano: ${option.year}`
                                    }
                                    filterOptions={(options, state) => {
                                        const inputValue = state.inputValue.toLowerCase().trim();
                                        if (!inputValue) return options;

                                        return options.filter(option =>
                                            option.number?.toString().toLowerCase().includes(inputValue) ||
                                            option.numberProcess?.toString().toLowerCase().includes(inputValue) ||
                                            option.year?.toString().includes(inputValue) ||
                                            option.modality?.toLowerCase().includes(inputValue) ||
                                            option.disputeMode?.toLowerCase().includes(inputValue)
                                        );
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.5, textAlign: 'left' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'left', width: '100%' }}>
                                                Nº {option.number} - Processo: {option.numberProcess}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', width: '100%' }}>
                                                Exercício: {option.year} | Modalidade: {option.modality}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left', width: '100%' }}>
                                                Modo de Disputa: {option.disputeMode}
                                            </Typography>
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Digite para buscar..."
                                            error={showValidationErrors && !contractRequest.purchaseId}
                                            required
                                            inputProps={{
                                                ...params.inputProps,
                                                style: { textAlign: 'left' }
                                            }}
                                        />
                                    )}
                                    noOptionsText="Nenhuma compra encontrada"
                                />
                            </Grid>
                            {/* Seção: Classificação */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Classificação
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item lg={7} sm={12} md={6} xl={7}>
                                <InputLabel required>Unidade</InputLabel>
                                <Select
                                    autoComplete='off'
                                    onChange={handleOnChange}
                                    fullWidth
                                    error={showValidationErrors && (!contractRequest.codBuyerUnit || contractRequest.codBuyerUnit.length === 0)}
                                    value={contractRequest.codBuyerUnit ?? ''}
                                    name='select-unid-entity'
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>
                                    {unidEntity.map((element, index) => {
                                        return (
                                            <MenuItem value={element.unitCode} key={element.unitCode}>{element.unitCode} - {element.name}</MenuItem>
                                        );
                                    })}
                                </Select>
                            </Grid>

                            <Grid item lg={5} sm={12} md={6} xl={5}>
                                <InputLabel required>Tipo de Contrato</InputLabel>
                                <Select
                                    autoComplete='off'
                                    onChange={handleOnChange}
                                    fullWidth
                                    error={showValidationErrors && !contractRequest.contractTypeId}
                                    value={contractRequest.contractTypeId ?? ''}
                                    name='select-contract-type'
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>
                                    {typeContract.map((element, index) => {
                                        return (
                                            <MenuItem value={Number(element.id)} key={element.id}>{element.id} - {element.name}</MenuItem>
                                        );
                                    })}
                                </Select>
                            </Grid>

                            <Grid item lg={6} sm={12} md={6} xl={6}>
                                <InputLabel required>Categoria</InputLabel>
                                <Select
                                    autoComplete='off'
                                    onChange={handleOnChange}
                                    fullWidth
                                    error={showValidationErrors && !contractRequest.categoryProcessId}
                                    value={contractRequest.categoryProcessId ?? ''}
                                    name='select-cateogry-process'
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>
                                    {categoryProcess.map((element, index) => {
                                        return (
                                            <MenuItem value={Number(element.id)} key={element.id}>{element.id} - {element.name}</MenuItem>
                                        );
                                    })}

                                </Select>
                            </Grid>

                            {/* Seção: Datas e Vigência */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Datas e Vigência
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item lg={2} sm={6} md={4} xl={2}>
                                <InputLabel required>Data de Assinatura</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off'
                                        error={showValidationErrors && (!contractRequest.signDate || contractRequest.signDate.length === 0)}
                                        required
                                    />}

                                    value={contractRequest.signDate !== undefined ? moment(contractRequest.signDate) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('signDate', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>
                            <Grid item lg={2} sm={6} md={4} xl={2}>
                                <InputLabel required>Inicio de Vigência</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off'
                                        error={showValidationErrors && (!contractRequest.effectiveDateStart || contractRequest.effectiveDateStart.length === 0)}
                                        required
                                    />}

                                    value={contractRequest.effectiveDateStart !== undefined ? moment(contractRequest.effectiveDateStart) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('effectiveDateStart', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>

                            <Grid item lg={2} sm={6} md={4} xl={2}>
                                <InputLabel required>Fim de Vigência</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off'
                                        error={showValidationErrors && (!contractRequest.effectiveDateEnd || contractRequest.effectiveDateEnd.length === 0)}
                                        required
                                    />}

                                    value={contractRequest.effectiveDateEnd !== undefined ? moment(contractRequest.effectiveDateEnd) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('effectiveDateEnd', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>

                            {/* Seção: Dados do Fornecedor */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Dados do Fornecedor
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel required>Tipo Fornecedor</InputLabel>
                                <Select
                                    autoComplete='off'
                                    onChange={handleOnChange}
                                    fullWidth
                                    error={showValidationErrors && (!contractRequest.supplierType || contractRequest.supplierType.length === 0)}
                                    value={contractRequest.supplierType ?? ''}
                                    name='select-supplier-type'
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>

                                    <MenuItem value="PJ" key="PJ">Pessoa Juridica</MenuItem>
                                    <MenuItem value="PF" key="PF">Pessoa Fisica</MenuItem>
                                    <MenuItem value="PE" key="PE">Pessoa Estrangeiro</MenuItem>
                                </Select>
                            </Grid>
                            <Grid item lg={3} sm={6} md={4} xl={3}>
                                <InputLabel required>{contractRequest.supplierType == 'PF' ? 'CPF do Fornecedor' : contractRequest.supplierType == 'PE' ? 'Identificação do Fornecedor Estrangeiro' : 'CNPJ do Fornecedor'}</InputLabel>
                                <InputMask mask={contractRequest.supplierType == 'PF' ? '999.999.999-99' : contractRequest.supplierType == 'PE' ? '' : '99.999.999/9999-99'}
                                    name="input-supplier-number-identification"
                                    id="input-supplier-number-identification"
                                    autoComplete='off'
                                    value={contractRequest.supplierNumberIdentification || ''}
                                    onChange={handleOnChange}>
                                    <TextField fullWidth
                                        error={showValidationErrors && (!contractRequest.supplierNumberIdentification || contractRequest.supplierNumberIdentification.length === 0)}
                                        name="input-supplier-number-identification"
                                        id="input-supplier-number-identification"
                                        value={contractRequest.supplierNumberIdentification || ''}
                                        onChange={handleOnChange}
                                        required />
                                </InputMask>
                            </Grid>
                            <Grid item lg={7} sm={12} md={12} xl={7}>
                                <InputLabel required>Nome do Fornecedor</InputLabel>
                                <OutlinedInput
                                    name="input-supplier-name"
                                    autoComplete='off'
                                    fullWidth
                                    error={showValidationErrors && (!contractRequest.supplierName || contractRequest.supplierName.length === 0)}
                                    value={contractRequest.supplierName || ''}
                                    onChange={handleOnChange}
                                    required
                                />
                            </Grid>

                            {/* Seção: Fornecedor Subcontratado (Opcional) */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Fornecedor Subcontratado (Opcional)
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel>Tipo Fornecedor Subcontratato</InputLabel>
                                <Select
                                    onChange={handleOnChange}
                                    autoComplete='off'
                                    fullWidth
                                    value={contractRequest.supplierTypeSubContract ?? ''}
                                    name='select-supplier-type-subcontract'
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>

                                    <MenuItem value="PJ" key="PJ">Pessoa Juridica</MenuItem>
                                    <MenuItem value="PF" key="PF">Pessoa Fisica</MenuItem>
                                    <MenuItem value="PE" key="PE">Pessoa Estrangeiro</MenuItem>

                                </Select>
                            </Grid>
                            <Grid item lg={3} sm={6} md={4} xl={3}>
                                <InputLabel>{contractRequest.supplierTypeSubContract == 'PF' ? 'CPF do Fornecedor Subcontratado' : contractRequest.supplierTypeSubContract == 'PE' ? 'Identificação do Fornecedor Estrangeiro Subcontratado' : 'CNPJ do Fornecedor Subcontratado'}</InputLabel>
                                <InputMask mask={contractRequest.supplierTypeSubContract == 'PF' ? '999.999.999-99' : contractRequest.supplierTypeSubContract == 'PE' ? '' : '99.999.999/9999-99'}
                                    name="input-supplier-number-identification-subcontract"
                                    autoComplete='off'
                                    id="input-supplier-number-identification-subcontract"
                                    value={contractRequest.supplierNumberIdentificationSubContract || ''}
                                    onChange={handleOnChange}>
                                    <TextField fullWidth
                                        name="input-supplier-number-identification-subcontract"
                                        id="input-supplier-number-identification-subcontract"
                                        value={contractRequest.supplierNumberIdentificationSubContract || ''}
                                        onChange={handleOnChange} />
                                </InputMask>
                            </Grid>

                            <Grid item lg={7} sm={12} md={12} xl={7}>
                                <InputLabel>Nome do Fornecedor Subcontratado</InputLabel>
                                <OutlinedInput
                                    name="input-supplier-name-subcontract"
                                    autoComplete='off'
                                    fullWidth
                                    value={contractRequest.supplierNameSubContract || ''}
                                    onChange={handleOnChange}
                                />
                            </Grid>

                            {/* Seção: Dados CIP (Opcional) */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Dados CIP (Opcional)
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item lg={4} sm={12} md={6} xl={4}>
                                <InputLabel>Idenficador no CIP</InputLabel>
                                <OutlinedInput
                                    name="input-identfiy-cipi"
                                    autoComplete='off'
                                    fullWidth
                                    value={contractRequest.identifyCIPI || ''}
                                    onChange={handleOnChange}
                                />
                            </Grid>
                            <Grid item lg={8} sm={12} md={12} xl={8}>
                                <InputLabel>Url no CIP</InputLabel>
                                <OutlinedInput
                                    name="input-url-cipi"
                                    autoComplete='off'
                                    fullWidth
                                    value={contractRequest.urlCIPI || ''}
                                    onChange={handleOnChange}
                                />
                            </Grid>

                            {/* Seção: Valores Financeiros */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Valores Financeiros
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel required>Valor Inicial</InputLabel>
                                <BrazilianNumberInput
                                    value={contractRequest?.initialValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-initial-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    error={showValidationErrors && (contractRequest?.initialValue === undefined || contractRequest?.initialValue === 0)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item lg={1} sm={6} md={2} xl={1}>
                                <InputLabel required>Nº Parcelas</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-number-payment-installment"
                                    error={showValidationErrors && (!contractRequest.numberPaymentInstallment || contractRequest.numberPaymentInstallment === 0)}
                                    value={contractRequest.numberPaymentInstallment || 0}
                                    type={"number"}
                                    inputProps={{ min: 1, max: 1000 }}
                                    onChange={handleOnChange}
                                    required
                                />
                            </Grid>

                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel>Valor Parcela</InputLabel>
                                <BrazilianNumberInput
                                    value={contractRequest?.paymentInstallmentlValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-payment-installment-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    error={showValidationErrors && (contractRequest?.paymentInstallmentlValue === undefined || contractRequest?.paymentInstallmentlValue === 0)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel required>Valor Global</InputLabel>
                                <BrazilianNumberInput
                                    value={contractRequest?.globalValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-global-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    error={showValidationErrors && (contractRequest?.globalValue === undefined || contractRequest?.globalValue === 0)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item lg={2} sm={6} md={3} xl={2}>
                                <InputLabel required>Valor Acumulado</InputLabel>
                                <BrazilianNumberInput
                                    value={contractRequest?.accumulatedValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-acumulated-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    error={showValidationErrors && (contractRequest?.accumulatedValue === undefined || contractRequest?.accumulatedValue === 0)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>

                                <Box>
                                    <Box>
                                        <Tabs value={tabIndex} onChange={(e, y) => handleTabChange(e, y)}>
                                            <Tab label="Objeto" />
                                            <Tab label="Mais Informações" />
                                            <Tab label="Arquivos" />
                                        </Tabs>
                                    </Box>
                                </Box>
                                <Box sx={{ pt: 2 }}>
                                    {tabIndex === 0 && (
                                        <Box>
                                            <OutlinedInput
                                                autoComplete='off'
                                                multiline
                                                rows={5}
                                                onChange={handleOnChange}
                                                error={showValidationErrors && (!contractRequest.object || contractRequest.object.length === 0)}
                                                value={contractRequest.object || ''}
                                                name='input-object'
                                                fullWidth
                                                required
                                            />

                                        </Box>
                                    )}
                                    {tabIndex === 1 && (
                                        <Box>
                                            <OutlinedInput
                                                autoComplete='off'
                                                multiline
                                                rows={5}
                                                onChange={handleOnChange}
                                                value={contractRequest.notes || ''}
                                                name='input-notes'
                                                fullWidth
                                            />

                                        </Box>
                                    )}
                                    {tabIndex === 2 && (
                                        <Paper
                                            sx={{
                                                p: 2,

                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <Box sx={{
                                                minHeight: "350px", display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                {filesDataTable()}
                                            </Box>
                                        </Paper>
                                    )}
                                </Box>

                            </Grid>
                            <Grid item lg={12}
                                sm={12}
                                xl={12}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                    <Box sx={{ flex: '1 1 auto' }} />
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        endIcon={<SaveIcon />}
                                    >
                                        Salvar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                    </LocalizationProvider>

                </Paper>
            </Grid>
        </React.Fragment >
    );
}